import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Phone, ArrowLeft, Loader2, Shield, MapPin, Check,
  ChevronRight, User, Stethoscope, Building2, FlaskConical,
  Edit2, Activity
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { API_URL } from '../config/api';

const COUNTRY_CODES = [
  { code: '+91', country: 'India', flag: 'IN' },
  { code: '+971', country: 'UAE', flag: 'AE' },
  { code: '+1', country: 'USA', flag: 'US' },
  { code: '+44', country: 'UK', flag: 'GB' },
  { code: '+966', country: 'Saudi Arabia', flag: 'SA' },
  { code: '+65', country: 'Singapore', flag: 'SG' },
  { code: '+61', country: 'Australia', flag: 'AU' },
  { code: '+92', country: 'Pakistan', flag: 'PK' },
  { code: '+880', country: 'Bangladesh', flag: 'BD' },
  { code: '+977', country: 'Nepal', flag: 'NP' },
  { code: '+94', country: 'Sri Lanka', flag: 'LK' },
];

const ROLES = [
  { id: 'user', label: 'User', icon: User, desc: 'Book appointments & track health' },
  { id: 'doctor', label: 'Doctor', icon: Stethoscope, desc: 'Manage patients & consultations' },
  { id: 'clinic', label: 'Clinic', icon: Building2, desc: 'Hospital & clinic management' },
  { id: 'lab', label: 'Lab', icon: FlaskConical, desc: 'Lab tests & reports' },
];

const PhoneLogin = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(1); // 1: phone, 2: otp, 3: profile confirm
  const [countryCode, setCountryCode] = useState('+91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [devOtp, setDevOtp] = useState('');
  const [location, setLocation] = useState(null);
  const [existingUser, setExistingUser] = useState(false);
  const [userName, setUserName] = useState('');
  const [selectedRole, setSelectedRole] = useState('user');
  const [timer, setTimer] = useState(0);
  const [editingLocation, setEditingLocation] = useState(false);
  const [editLocation, setEditLocation] = useState({ country: '', state: '', city: '' });
  const otpRefs = useRef([]);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer(t => t - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const fullPhone = `${countryCode}${phoneNumber}`;

  const handleSendOtp = async () => {
    if (phoneNumber.length < 7) {
      setError('Please enter a valid phone number');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: fullPhone }),
        credentials: 'include'
      });
      const data = await res.json();
      if (res.ok) {
        setLocation(data.location);
        setExistingUser(data.existing_user);
        if (data.user_name) setUserName(data.user_name);
        if (data.user_role) setSelectedRole(data.user_role);
        if (data.dev_otp) setDevOtp(data.dev_otp);
        if (data.location) {
          setEditLocation({
            country: data.location.country || '',
            state: data.location.state || '',
            city: data.location.city || ''
          });
        }
        setTimer(data.expires_in_seconds || 300);
        setStep(2);
      } else {
        setError(data.detail || 'Failed to send OTP');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) value = value[value.length - 1];
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const code = otp.join('');
    if (code.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_number: fullPhone,
          otp_code: code,
          role: selectedRole,
          name: userName
        }),
        credentials: 'include'
      });
      const data = await res.json();
      if (res.ok && data.status === 'verified') {
        if (data.is_new_user) {
          setLocation(data.location_detected);
          setStep(3);
        } else {
          await refreshUser();
          const role = data.user?.role || 'user';
          if (role === 'admin') navigate('/admin/dashboard');
          else navigate('/dashboard/health');
        }
      } else {
        setError(data.detail || 'Verification failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  const handleCompleteProfile = async () => {
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/auth/otp/update-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_number: fullPhone,
          name: userName,
          role: selectedRole,
          country: editLocation.country,
          state: editLocation.state,
          city: editLocation.city
        }),
        credentials: 'include'
      });
      await refreshUser();
      navigate('/dashboard/health');
    } catch (err) {
      await refreshUser();
      navigate('/dashboard/health');
    }
    setLoading(false);
  };

  const handleResendOtp = async () => {
    if (timer > 0) return;
    setOtp(['', '', '', '', '', '']);
    setDevOtp('');
    await handleSendOtp();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex" data-testid="phone-login-page">
      {/* Left panel */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md">
          {/* Back button */}
          <button
            onClick={() => step > 1 ? setStep(step - 1) : navigate('/login/health')}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 text-sm"
            data-testid="phone-login-back"
          >
            <ArrowLeft className="w-4 h-4" />
            {step > 1 ? 'Back' : 'Other login options'}
          </button>

          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">HealthTrack <span className="text-orange-500">Pro</span></span>
          </div>

          {/* Step 1: Phone Number */}
          {step === 1 && (
            <div data-testid="phone-step-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Login with Phone</h2>
              <p className="text-gray-500 mb-8">We'll send you a one-time verification code</p>

              {/* Role Selection */}
              <div className="mb-6">
                <label className="text-sm font-medium text-gray-700 mb-2 block">I am a</label>
                <div className="grid grid-cols-2 gap-2">
                  {ROLES.map(role => (
                    <button
                      key={role.id}
                      onClick={() => setSelectedRole(role.id)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        selectedRole === role.id
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`role-${role.id}`}
                    >
                      <role.icon className={`w-5 h-5 mb-1 ${selectedRole === role.id ? 'text-orange-500' : 'text-gray-400'}`} />
                      <div className={`text-sm font-medium ${selectedRole === role.id ? 'text-orange-700' : 'text-gray-700'}`}>{role.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Phone Input */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700 mb-2 block">Phone Number</label>
                <div className="flex gap-2">
                  <select
                    value={countryCode}
                    onChange={e => setCountryCode(e.target.value)}
                    className="px-3 py-3 border border-gray-300 rounded-xl bg-white text-sm font-medium w-24"
                    data-testid="country-code-select"
                  >
                    {COUNTRY_CODES.map(cc => (
                      <option key={cc.code} value={cc.code}>{cc.code} {cc.flag}</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={e => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                    placeholder="Enter phone number"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-lg tracking-wider focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                    maxLength={12}
                    data-testid="phone-input"
                  />
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm" data-testid="error-msg">
                  {error}
                </div>
              )}

              <Button
                onClick={handleSendOtp}
                disabled={loading || phoneNumber.length < 7}
                className="w-full py-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-semibold text-base"
                data-testid="send-otp-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : (
                  <span className="flex items-center justify-center gap-2">
                    Send OTP <ChevronRight className="w-4 h-4" />
                  </span>
                )}
              </Button>

              <div className="mt-6 text-center">
                <Link to="/login/health" className="text-sm text-orange-600 hover:text-orange-700 font-medium">
                  Login with Email instead
                </Link>
              </div>
            </div>
          )}

          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <div data-testid="phone-step-2">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify OTP</h2>
              <p className="text-gray-500 mb-2">Enter the 6-digit code sent to</p>
              <p className="text-orange-600 font-semibold mb-6">{fullPhone}</p>

              {/* Location detected banner */}
              {location?.detected && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-blue-500" />
                  <span className="text-blue-700">
                    Location detected: <strong>{[location.city, location.state, location.country].filter(Boolean).join(', ')}</strong>
                  </span>
                </div>
              )}

              {/* Dev OTP hint */}
              {devOtp && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                  <strong>Dev Mode:</strong> OTP is <span className="font-mono font-bold text-lg">{devOtp}</span>
                </div>
              )}

              {/* OTP Input */}
              <div className="flex gap-2 mb-6 justify-center">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => otpRefs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleOtpChange(i, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(i, e)}
                    className="w-12 h-14 text-center text-xl font-bold border-2 border-gray-300 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition"
                    data-testid={`otp-input-${i}`}
                  />
                ))}
              </div>

              {/* Name input for new users */}
              {!existingUser && (
                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Your Name</label>
                  <input
                    type="text"
                    value={userName}
                    onChange={e => setUserName(e.target.value)}
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                    data-testid="name-input"
                  />
                </div>
              )}

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                  {error}
                </div>
              )}

              <Button
                onClick={handleVerifyOtp}
                disabled={loading || otp.join('').length !== 6}
                className="w-full py-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-semibold"
                data-testid="verify-otp-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : (
                  <span className="flex items-center justify-center gap-2">
                    <Shield className="w-4 h-4" /> Verify & {existingUser ? 'Login' : 'Create Account'}
                  </span>
                )}
              </Button>

              <div className="mt-4 text-center">
                <button
                  onClick={handleResendOtp}
                  disabled={timer > 0}
                  className={`text-sm ${timer > 0 ? 'text-gray-400' : 'text-orange-600 hover:text-orange-700'}`}
                  data-testid="resend-otp-btn"
                >
                  {timer > 0 ? `Resend in ${Math.floor(timer / 60)}:${(timer % 60).toString().padStart(2, '0')}` : 'Resend OTP'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Profile Confirmation (new users) */}
          {step === 3 && (
            <div data-testid="phone-step-3">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Phone Verified!</h2>
                <p className="text-gray-500 mt-1">Confirm your details to complete registration</p>
              </div>

              {/* Auto-filled profile */}
              <div className="space-y-3 mb-6">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Name</label>
                  <input
                    type="text"
                    value={userName}
                    onChange={e => setUserName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none"
                    data-testid="confirm-name"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Phone</label>
                  <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-green-500" />
                    {fullPhone}
                    <Check className="w-4 h-4 text-green-500 ml-auto" />
                  </div>
                </div>

                {/* Location (auto-detected, editable) */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-gray-700">Location (auto-detected)</label>
                    <button
                      onClick={() => setEditingLocation(!editingLocation)}
                      className="text-xs text-orange-600 flex items-center gap-1"
                    >
                      <Edit2 className="w-3 h-3" /> Edit
                    </button>
                  </div>
                  {!editingLocation ? (
                    <div className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-blue-700 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {[editLocation.city, editLocation.state, editLocation.country].filter(Boolean).join(', ') || 'Not detected'}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editLocation.country}
                        onChange={e => setEditLocation({...editLocation, country: e.target.value})}
                        placeholder="Country"
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                      <input
                        type="text"
                        value={editLocation.state}
                        onChange={e => setEditLocation({...editLocation, state: e.target.value})}
                        placeholder="State"
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                      <input
                        type="text"
                        value={editLocation.city}
                        onChange={e => setEditLocation({...editLocation, city: e.target.value})}
                        placeholder="City"
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                    </div>
                  )}
                </div>

                {/* Role */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Account Type</label>
                  <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 capitalize">
                    {ROLES.find(r => r.id === selectedRole)?.label || selectedRole}
                  </div>
                </div>
              </div>

              <Button
                onClick={handleCompleteProfile}
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-semibold"
                data-testid="complete-profile-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Complete Registration'}
              </Button>
            </div>
          )}

          {/* Footer */}
          <p className="text-xs text-gray-400 text-center mt-8">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>

      {/* Right panel - branding (desktop only) */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-orange-500 to-amber-600 items-center justify-center p-12">
        <div className="max-w-lg text-white">
          <Phone className="w-16 h-16 mb-6" />
          <h1 className="text-4xl font-bold mb-4">Quick & Secure Login</h1>
          <p className="text-xl text-orange-100 mb-8">
            No passwords needed. Just your phone number and a one-time code.
          </p>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-sm font-bold">1</div>
              <span className="text-orange-100">Enter your phone number</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-sm font-bold">2</div>
              <span className="text-orange-100">Receive OTP via SMS</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-sm font-bold">3</div>
              <span className="text-orange-100">Location auto-detected from number</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-sm font-bold">4</div>
              <span className="text-orange-100">Matched with nearby doctors & labs</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneLogin;
